"use client";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Link,
  List,
  Plus,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import Notiflix from "notiflix";
import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../services/apicollection";
import {
  deleteLectureAPI,
  getLecturesByCourseIdAPI,
} from "../services/apicollection";
import toast from "react-hot-toast";

const LectureForm = ({ courseLectures, setCourseLectures }) => {
  const [courseId, setCourseId] = useState("");
  const [lectureData, setLectureData] = useState({
    name: "",
    videoLink: "",
    videoFile: null,
    videoType: "link", // 'link' or 'upload'
    topics: [],
    duration: "",
    description: "",
  });
  const [editingLecture, setEditingLecture] = useState(null);
  const [showLectureForm, setShowLectureForm] = useState(false);
  const [expandedLecture, setExpandedLecture] = useState(null);
  const [courses, setCourses] = useState([]);

  const [currentTopic, setCurrentTopic] = useState("");
  const [currentTopicNotes, setCurrentTopicNotes] = useState("");
  const [currentTopicImage, setCurrentTopicImage] = useState("");
  const [currentTopicCode, setCurrentTopicCode] = useState("");
  const [currentTopicCodeLang, setCurrentTopicCodeLang] = useState("javascript");
  const [currentTopicTryIt, setCurrentTopicTryIt] = useState(false);
  const videoInputRef = useRef(null);

  const handleLectureInputChange = (field, value) => {
    setLectureData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check if file is a video
      if (!file.type.startsWith("video/")) {
        alert("Please select a valid video file");
        return;
      }

      // Check file size (limit to 500MB)
      // if (file.size > 500 * 1024 * 1024) {
      //   alert("Video file size should be less than 500MB");
      //   return;
      // }

      const reader = new FileReader();
      reader.onload = (e) => {
        setLectureData((prev) => ({
          ...prev,
          videoFile: {
            file,
            url: e.target?.result,
            name: file.name,
            size: file.size,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoTypeChange = (type) => {
    setLectureData((prev) => ({
      ...prev,
      videoType: type,
      videoLink: type === "upload" ? "" : prev.videoLink,
      videoFile: type === "link" ? null : prev.videoFile,
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const addTopic = () => {
    if (
      currentTopic.trim() &&
      !lectureData.topics.some((t) => t.topic === currentTopic.trim())
    ) {
      setLectureData((prev) => ({
        ...prev,
        topics: [
          ...prev.topics,
          {
            topic: currentTopic.trim(),
            notes: currentTopicNotes.trim(),
            imageUrl: currentTopicImage.trim(),
            codeSnippet: currentTopicCode.trim(),
            codeLanguage: currentTopicCodeLang,
            tryItYourself: currentTopicTryIt,
          },
        ],
      }));
      setCurrentTopic("");
      setCurrentTopicNotes("");
      setCurrentTopicImage("");
      setCurrentTopicCode("");
      setCurrentTopicCodeLang("javascript");
      setCurrentTopicTryIt(false);
    }
  };

  // Quick-fill placeholder topics+notes so the student panel isn't blank
  // while you're still writing the real content — edit/replace these
  // before actually publishing the lecture.
  const fillDummyTopics = () => {
    const dummy = [
      {
        topic: "Introduction & Overview",
        notes:
          "DUMMY NOTES — replace before publishing. Brief overview of what this lecture covers and why it matters.",
      },
      {
        topic: "Key Concepts",
        notes:
          "DUMMY NOTES — replace before publishing. List the main concepts/terms introduced in this lecture with short explanations.",
      },
      {
        topic: "Step-by-step Walkthrough",
        notes:
          "DUMMY NOTES — replace before publishing. Break down the practical steps/code shown in the video.",
      },
      {
        topic: "Common Mistakes",
        notes:
          "DUMMY NOTES — replace before publishing. Note any mistakes students commonly make on this topic.",
      },
      {
        topic: "Summary & Practice",
        notes:
          "DUMMY NOTES — replace before publishing. Quick recap + a practice task or question for students.",
      },
    ];
    setLectureData((prev) => ({
      ...prev,
      topics: [...prev.topics, ...dummy],
    }));
  };

  const removeTopic = (topicToRemove) => {
    setLectureData((prev) => ({
      ...prev,
      topics: prev.topics.filter((t) => t.topic !== topicToRemove),
    }));
  };

  const handleSaveLecture = async (e) => {
    // console.log(e);
    e.preventDefault();
    if (
      !lectureData.name ||
      (!lectureData.videoLink && !lectureData.videoFile)
    ) {
      alert(
        "Please fill in lecture name and provide either a video link or upload a video file",
      );
      return;
    }

    const newLecture = {
      ...lectureData,
      id: editingLecture ? editingLecture.id : Date.now(),
      createdAt: editingLecture
        ? editingLecture.createdAt
        : new Date().toISOString(),
    };

    // console.log("data to post", newLecture);

    // Real FormData is required here — a plain object with a
    // "multipart/form-data" header (as before) doesn't actually get
    // multipart-encoded by axios; it just gets JSON.stringified while the
    // header lies about the content type, and multer on the backend can't
    // parse a JSON body as multipart. This silently broke the video file
    // upload and any other fields whenever a file was attached.
    const payload = new FormData();
    payload.append("name", lectureData?.name || "");
    payload.append("description", lectureData?.description || "");
    payload.append("courseId", courseId);
    payload.append("duration", lectureData?.duration || "");
    payload.append("topics", JSON.stringify(lectureData.topics || []));
    if (lectureData?.videoFile?.file) {
      payload.append("lecturevideo", lectureData.videoFile.file);
    }
    if (newLecture?.videoLink) {
      payload.append("videoUrl", newLecture.videoLink);
    }

    try {
      let response = await axiosInstance.post(
        `${apiUrl.POSTLECTURE}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data", // 👈 very important
          },
        },
      );

      if (!response?.data?.status) {
        return Notiflix.Notify.failure("failed to add lecture");
      }

      setCourseLectures((prev) => [...prev, newLecture]);

      // Reset lecture form
      setLectureData({
        name: "",
        videoLink: "",
        videoFile: null,
        videoType: "link",
        topics: [],
        duration: "",
        description: "",
      });
      setShowLectureForm(false);
      setEditingLecture(null);
    } catch (error) {
      Notiflix.Notify(`something went wrong ! Error : ${error.message}`);
    }
  };

  const handleEditLecture = (lecture) => {
    setLectureData(lecture);
    setEditingLecture(lecture);
    setShowLectureForm(true);
  };

  const handleDeleteLecture = (lectureId) => {
    // console.log("items to delete", lectureId);
    if (window.confirm("Are you sure you want to delete this lecture?")) {
      deleteLectureAPI(lectureId);
    }
  };

  const handleCancelLecture = () => {
    setLectureData({
      name: "",
      videoLink: "",
      videoFile: null,
      videoType: "link",
      topics: [],
      duration: "",
      description: "",
    });
    setShowLectureForm(false);
    setEditingLecture(null);
  };

  useEffect(() => {
    async function getCourse() {
      let res = await axiosInstance.get(apiUrl.GETCOURSES);
      setCourses(res.data.data);
    }
    getCourse();
  }, []);

  useEffect(() => {
    async function getCourseLecture() {
      let res = await getLecturesByCourseIdAPI(courseId);
      if (res.status) {
        setCourseLectures(res.data);
        toast.success(res.message);
      }
    }
    getCourseLecture();
  }, [courseId]);

  return (
    <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
      <div className="flex items-center justify-between mb-6">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
          <Video size={20} className="text-blue-600" />
          Course Lectures ({courseLectures?.length})
        </h3>
        <div className="flex gap-4">
          <select
            // value={filterCategory}
            onChange={(e) => setCourseId(e.target.value)}
            className="px-4 py-2 bg-[#0d1220] text-slate-100 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value={""}>-- select course --</option>
            {courses.map((course, index) => (
              <option key={index} value={course._id}>
                {course?.courseName}
              </option>
            ))}
          </select>
        </div>

        {courseId && (
          <button
            type="button"
            onClick={() => setShowLectureForm(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
          >
            <Plus size={18} />
            Add Lecture
          </button>
        )}
      </div>

      {/* Lecture Form */}
      {showLectureForm && (
        <div className="mb-6 p-4 bg-[#0d1b2e] rounded-lg border border-[#1d3350]">
          <h4 className="text-md font-semibold text-slate-100 mb-4">
            {editingLecture ? "Edit Lecture" : "Add New Lecture"}
          </h4>
          <form onSubmit={handleSaveLecture} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Lecture Name
                </label>
                <input
                  type="text"
                  value={lectureData?.name}
                  onChange={(e) =>
                    handleLectureInputChange("name", e.target.value)
                  }
                  placeholder="Enter lecture name..."
                  className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={lectureData.duration}
                  onChange={(e) =>
                    handleLectureInputChange("duration", e.target.value)
                  }
                  placeholder="30"
                  className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Video Upload/Link Section */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Video Content
              </label>

              {/* Video Type Selection */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => handleVideoTypeChange("link")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300 ${
                    lectureData.videoType === "link"
                      ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                      : "bg-[#0d1b2e] border-[#1d3350] text-slate-400 hover:bg-[#13233a]"
                  }`}
                >
                  <Link size={16} />
                  <span>Video Link</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleVideoTypeChange("upload")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300 ${
                    lectureData.videoType === "upload"
                      ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                      : "bg-[#0d1b2e] border-[#1d3350] text-slate-400 hover:bg-[#13233a]"
                  }`}
                >
                  <Upload size={16} />
                  <span>Upload Video</span>
                </button>
              </div>

              {/* Video Link Input */}
              {lectureData.videoType === "link" && (
                <div>
                  <input
                    type="url"
                    value={lectureData.videoLink}
                    onChange={(e) =>
                      handleLectureInputChange("videoLink", e.target.value)
                    }
                    placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                    className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Supports YouTube, Vimeo, and other video platforms
                  </p>
                </div>
              )}

              {/* Video Upload */}
              {lectureData.videoType === "upload" && (
                <div>
                  {!lectureData.videoFile ? (
                    <div
                      className="border-2 border-dashed border-[#1d3350] rounded-lg p-6 text-center hover:border-slate-500 transition-colors cursor-pointer"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <Video size={48} className="text-gray-400 mx-auto mb-4" />
                      <div>
                        <p className="text-slate-400 mb-2">
                          Click to upload video or drag and drop
                        </p>
                        <p className="text-xs text-slate-400">
                          Supports MP4, MOV, AVI, WMV up to 500MB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-[#1d3350] rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Video size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-100">
                              {lectureData.videoFile.name}
                            </p>
                            <p className="text-sm text-slate-400">
                              {formatFileSize(lectureData.videoFile.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setLectureData((prev) => ({
                              ...prev,
                              videoFile: null,
                            }))
                          }
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  )}

                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Description
              </label>
              <textarea
                value={lectureData.description}
                onChange={(e) =>
                  handleLectureInputChange("description", e.target.value)
                }
                placeholder="Brief description of the lecture..."
                rows={3}
                className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Topics Covered{" "}
                <span className="text-slate-500 text-xs font-normal">
                  (add course notes for each topic)
                </span>
              </label>
              <div className="flex flex-col gap-2 mb-2">
                <input
                  type="text"
                  value={currentTopic}
                  onChange={(e) => setCurrentTopic(e.target.value)}
                  placeholder="Topic title, e.g. 'Introduction to Hooks'"
                  className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <textarea
                  value={currentTopicNotes}
                  onChange={(e) => setCurrentTopicNotes(e.target.value)}
                  placeholder="Notes for this topic (plain text, or a link to a notes PDF/doc)"
                  rows={2}
                  className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <input
                  type="text"
                  value={currentTopicImage}
                  onChange={(e) => setCurrentTopicImage(e.target.value)}
                  placeholder="Image URL (diagram/screenshot) — optional"
                  className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <textarea
                  value={currentTopicCode}
                  onChange={(e) => setCurrentTopicCode(e.target.value)}
                  placeholder="Example code (shown in a code block) — optional"
                  rows={4}
                  className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={currentTopicCodeLang}
                    onChange={(e) => setCurrentTopicCodeLang(e.target.value)}
                    className="px-2 py-1.5 bg-[#0d1220] text-slate-100 border border-[#1d3350] rounded-lg text-sm"
                  >
                    <option value="javascript">JavaScript</option>
                    <option value="python">Python</option>
                    <option value="java">Java</option>
                    <option value="html">HTML</option>
                    <option value="css">CSS</option>
                    <option value="other">Other</option>
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-slate-400">
                    <input
                      type="checkbox"
                      checked={currentTopicTryIt}
                      onChange={(e) => setCurrentTopicTryIt(e.target.checked)}
                    />
                    Show "Try Yourself" editor
                  </label>
                </div>
                <button
                  type="button"
                  onClick={addTopic}
                  className="self-start px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  + Add topic
                </button>
                <button
                  type="button"
                  onClick={fillDummyTopics}
                  className="self-start px-3 py-2 bg-amber-600/20 text-amber-400 border border-amber-600/40 rounded-lg hover:bg-amber-600/30 transition-colors text-sm"
                >
                  + Fill sample placeholder topics (edit before publishing)
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {lectureData?.topics?.map((t, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between gap-2 bg-[#13233a] text-slate-200 px-3 py-2 rounded text-sm"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">{t.topic || t}</p>
                      {t.notes && (
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                          {t.notes}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeTopic(t.topic || t)}
                      className="text-slate-400 hover:text-slate-200 flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                {editingLecture ? "Update Lecture" : "Save Lecture"}
              </button>
              <div
                type="button"
                onClick={handleCancelLecture}
                className="bg-gray-300 text-slate-300 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Lectures List */}
      <div className="space-y-3">
        {courseLectures?.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Video size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No lectures added yet. Click "Add Lecture" to get started.</p>
          </div>
        ) : (
          <>
            {Array.isArray(courseLectures) &&
              courseLectures.map((lecture, index) => (
                <div
                  key={lecture._id}
                  className="border border-[#1d3350] rounded-lg"
                >
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded">
                        {index + 1}
                      </span>
                      <div>
                        <h4 className="font-medium text-slate-100">
                          {lecture.name}
                        </h4>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {lecture.duration} min
                          </span>
                          <span className="flex items-center gap-1">
                            <List size={14} />
                            {lecture.topics?.length} topics
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedLecture(
                            expandedLecture === lecture.id ? null : lecture.id,
                          )
                        }
                        className="p-2 text-gray-400 hover:text-slate-400 transition-colors"
                      >
                        {expandedLecture === lecture.id ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditLecture(lecture)}
                        className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteLecture(lecture._id)}
                        className="p-2 text-red-600 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {expandedLecture === lecture.id && (
                    <div className="px-4 pb-4 border-t border-[#17283f]">
                      <div className="pt-4 space-y-3">
                        {lecture.description && (
                          <p className="text-slate-400 text-sm">
                            {lecture.description}
                          </p>
                        )}
                        <div>
                          <span className="text-sm font-medium text-slate-300">
                            Video:{" "}
                          </span>
                          {lecture.videoFile ? (
                            <div className="flex items-center gap-2 mt-1">
                              <Video size={14} className="text-blue-600" />
                              <span className="text-sm text-slate-400">
                                {lecture.videoFile.name}
                              </span>
                              <span className="text-xs text-slate-400">
                                ({formatFileSize(lecture.videoFile.size)})
                              </span>
                            </div>
                          ) : (
                            <a
                              href={lecture.videoLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-sm break-all"
                            >
                              {lecture.videoLink}
                            </a>
                          )}
                        </div>
                        {lecture.topics?.length > 0 && (
                          <div>
                            <span className="text-sm font-medium text-slate-300">
                              Topics:{" "}
                            </span>
                            <div className="flex flex-col gap-1.5 mt-1">
                              {lecture.topics.map((t, topicIndex) => (
                                <div
                                  key={topicIndex}
                                  className="bg-[#13233a] text-slate-300 text-xs px-2 py-1.5 rounded"
                                >
                                  <p className="font-medium text-slate-200">
                                    {t.topic || t}
                                  </p>
                                  {t.notes && (
                                    <p className="text-slate-400 mt-0.5">
                                      {t.notes}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LectureForm;